import React, { useEffect, useRef } from "react";
import { useTheme } from "@/context/ThemeContext";

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let particles = [];

    // Configuration
    const PARTICLE_COUNT = 50;
    const CONNECTION_DISTANCE = 150;

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5; // -0.25 to 0.25
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1; // 1-3px
        this.opacity = Math.random() * 0.5 + 0.2; // 0.2-0.7
        this.opacityChange = (Math.random() - 0.5) * 0.005;
        this.originalOpacity = this.opacity;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around
        if (this.x < 0) this.x = canvas.width;
        if (this.x > canvas.width) this.x = 0;
        if (this.y < 0) this.y = canvas.height;
        if (this.y > canvas.height) this.y = 0;

        // Pulse opacity
        this.opacity += this.opacityChange;
        if (
          this.opacity > this.originalOpacity + 0.1 ||
          this.opacity < this.originalOpacity - 0.1
        ) {
          this.opacityChange = -this.opacityChange;
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);

        // Color based on theme
        if (isDarkMode) {
          // Glossy Green: Emerald-500 #10b981 (approx rgb: 16, 185, 129)
          ctx.fillStyle = `rgba(16, 185, 129, ${Math.max(0, this.opacity)})`;
        } else {
          // Earthy Green: rgba(45, 80, 22, opacity)
          ctx.fillStyle = `rgba(45, 80, 22, ${Math.max(0, this.opacity)})`;
        }

        ctx.fill();
      }
    }

    const init = () => {
      resizeCanvas();
      particles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      // Clear canvas completely each frame to prevent background artifacts/trails
      // The background color is handled by the CSS style on the canvas element.
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw Connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < CONNECTION_DISTANCE) {
            ctx.beginPath();
            const opacity = 0.1 * (1 - distance / CONNECTION_DISTANCE);

            if (isDarkMode) {
              // Dark glossy green connection
              ctx.strokeStyle = `rgba(16, 185, 129, ${opacity})`;
            } else {
              ctx.strokeStyle = `rgba(45, 80, 22, ${opacity})`;
            }

            ctx.lineWidth = 1;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Update and Draw Particles
      particles.forEach((p) => {
        p.update();
        p.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    window.addEventListener("resize", () => {
      resizeCanvas();
    });

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDarkMode]);

  // CSS Background Styles
  const backgroundStyle = isDarkMode
    ? {
        // Dark Mode: Linear gradient gray-800 to gray-900
        background: "linear-gradient(135deg, #1f2937, #111827)",
      }
    : {
        // Light Mode: Cream to Sage
        background:
          "linear-gradient(135deg, rgba(248, 246, 243, 0.95), rgba(213, 229, 219, 0.95))",
      };

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={backgroundStyle}
    />
  );
};

export default ParticleBackground;
